/**
 * Responsive layout helpers for the manual-fit strategy used once the game runs in
 * Phaser.Scale.RESIZE mode. The canvas then matches the real viewport (no engine
 * letterboxing), so each scene fits its 1280x720 design space onto the viewport by
 * driving its camera(s) directly, and fills any leftover margin with a full-bleed
 * backdrop instead of black bars.
 */
import type Phaser from "phaser";
import mapConfig from "../data/mapConfig.json";

export const DESIGN_WIDTH = mapConfig.designWidth;
export const DESIGN_HEIGHT = mapConfig.designHeight;

/** Uniform scale that fits the whole design rect inside the viewport (letterbox fit). */
export function fitScale(viewportW: number, viewportH: number): number {
  return Math.min(viewportW / DESIGN_WIDTH, viewportH / DESIGN_HEIGHT);
}

/**
 * Point a camera at the centre of the design space, scaled to fit the viewport.
 * @param zoomMultiplier extra zoom on top of the fit (1 = exactly fit; used by the
 *   world camera so the player's pinch/wheel zoom rides on top of the fit scale).
 */
export function fitCameraToDesign(
  camera: Phaser.Cameras.Scene2D.Camera,
  viewportW: number,
  viewportH: number,
  zoomMultiplier = 1
): void {
  // Guard against a 0-size viewport (e.g. a mobile rotation / address-bar transient):
  // fitScale would be 0 and setZoom(0) blanks the screen. Skip until a real size arrives.
  if (viewportW <= 0 || viewportH <= 0) return;
  camera.setViewport(0, 0, viewportW, viewportH);
  camera.setZoom(fitScale(viewportW, viewportH) * zoomMultiplier);
  camera.centerOn(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2);
}

/**
 * Display size (in world units) a backdrop centred on the design must have to cover the
 * whole viewport at the given fit, so the area outside the 1280x720 playfield is filled.
 */
export function backdropCoverSize(viewportW: number, viewportH: number): { width: number; height: number } {
  const scale = fitScale(viewportW, viewportH) || 1;
  // +2px slack avoids a hairline seam at the playfield edge from rounding.
  return { width: viewportW / scale + 2, height: viewportH / scale + 2 };
}

/**
 * Make a single-camera scene (menu/result/replay) responsive: fit its main camera to the
 * design rect now and on every resize, and paint the margins with a theme colour instead
 * of letting them fall back to black bars. Listeners are torn down on scene shutdown.
 *
 * Uses string event names ("resize"/"shutdown"/"destroy") so this module never needs the
 * Phaser runtime — keeping it importable from the node test environment.
 * @returns the apply() callback (call again manually after late layout changes if needed).
 */
/** The centred, fit-scaled rectangle the 1280x720 design occupies inside the viewport. */
export function letterboxRect(
  viewportW: number,
  viewportH: number
): { x: number; y: number; width: number; height: number; scale: number } {
  const scale = fitScale(viewportW, viewportH);
  const width = DESIGN_WIDTH * scale;
  const height = DESIGN_HEIGHT * scale;
  return { x: (viewportW - width) / 2, y: (viewportH - height) / 2, width, height, scale };
}

/**
 * Fit a HUD camera whose objects use setScrollFactor(0). Such objects ignore camera
 * scroll, so the standard centerOn() fit (which relies on scroll) would mis-place them
 * on any non-native width. Instead we give the camera origin (0,0) + zero scroll and a
 * letterbox-offset viewport, so a design point (x,y) maps exactly to
 * (letterboxLeft + scale*x, letterboxTop + scale*y) — aligned with the world playfield.
 */
export function fitHudCamera(camera: Phaser.Cameras.Scene2D.Camera, viewportW: number, viewportH: number): void {
  if (viewportW <= 0 || viewportH <= 0) return;
  const rect = letterboxRect(viewportW, viewportH);
  camera.setViewport(rect.x, rect.y, rect.width, rect.height);
  camera.setOrigin(0, 0);
  camera.setZoom(rect.scale);
  camera.setScroll(0, 0);
}

export function installLetterboxFit(scene: Phaser.Scene, backgroundColor: number | string = "#08070a"): () => void {
  scene.cameras.main.setBackgroundColor(backgroundColor);
  const apply = () => {
    const size = scene.scale.gameSize;
    fitCameraToDesign(scene.cameras.main, size.width, size.height, 1);
  };
  apply();
  scene.scale.on("resize", apply);
  scene.events.once("shutdown", () => scene.scale.off("resize", apply));
  scene.events.once("destroy", () => scene.scale.off("resize", apply));
  return apply;
}
